import { useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import Layout from "@/components/feature/Layout";
import Breadcrumb from "@/components/base/Breadcrumb";
import WorkCard from "@/components/base/WorkCard";
import NewsItem from "@/components/base/NewsItem";
import { worksList } from "@/mocks/works";
import { newsList } from "@/mocks/news";

type Tab = "work" | "news";

export default function Search() {
  const [searchParams, setSearchParams] = useSearchParams();
  const query = searchParams.get("q") ?? "";
  const [input, setInput] = useState(query);
  const [tab, setTab] = useState<Tab>("work");

  const kw = query.trim().toLowerCase();

  const workResults = useMemo(() => {
    if (!kw) return [];
    return worksList.filter(
      (w) =>
        w.title.toLowerCase().includes(kw) ||
        w.cast.join("").toLowerCase().includes(kw) ||
        w.genre.toLowerCase().includes(kw) ||
        w.tags.join("").toLowerCase().includes(kw),
    );
  }, [kw]);

  const newsResults = useMemo(() => {
    if (!kw) return [];
    return newsList.filter(
      (n) =>
        n.title.toLowerCase().includes(kw) || n.summary.toLowerCase().includes(kw),
    );
  }, [kw]);

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    const q = input.trim();
    setSearchParams(q ? { q } : {});
  };

  const hasQuery = kw.length > 0;
  const activeResults = tab === "work" ? workResults : newsResults;

  return (
    <Layout>
      <section className="w-full px-4 md:px-8 pt-6 md:pt-10 pb-6 border-b border-background-200 bg-background-100">
        <Breadcrumb items={[{ label: "搜索" }]} />
        <div className="mt-4 max-w-3xl">
          <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950">
            全站搜索
          </h1>
          <form
            onSubmit={submit}
            className="mt-4 flex flex-col sm:flex-row gap-3"
          >
            <div className="flex items-center gap-2 rounded-full px-4 h-12 bg-background-50 border border-background-300 flex-1">
              <i className="ri-search-line text-foreground-500 text-lg" />
              <input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="输入影视名称、演员、题材或资讯关键词"
                className="flex-1 bg-transparent text-sm text-foreground-900 placeholder:text-foreground-400 outline-none"
              />
            </div>
            <button
              type="submit"
              className="inline-flex items-center justify-center gap-2 px-6 h-12 rounded-md bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-medium transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-search-line" />
              搜索
            </button>
          </form>
          {hasQuery && (
            <p className="mt-3 text-sm text-foreground-600">
              关键词：
              <span className="text-primary-600 font-semibold">「{query}」</span>
              ，共找到 {workResults.length + newsResults.length} 条结果
            </p>
          )}
        </div>
      </section>

      <section className="w-full px-4 md:px-8 py-8 md:py-10">
        {!hasQuery ? (
          <div className="flex flex-col items-center justify-center py-20 text-center">
            <span className="w-16 h-16 rounded-full bg-background-100 text-foreground-400 flex items-center justify-center mb-4">
              <i className="ri-search-eye-line text-3xl" />
            </span>
            <h2 className="font-heading text-lg font-semibold text-foreground-800">
              输入关键词开始搜索
            </h2>
            <p className="mt-1 text-sm text-foreground-500">
              试试搜索「长夜」「悬疑」「电影」等关键词
            </p>
            <div className="mt-6 flex flex-col items-center gap-3">
              <span className="text-sm text-foreground-500">热门推荐</span>
              <div className="flex flex-wrap justify-center gap-2">
                {worksList.slice(0, 6).map((w) => (
                  <Link
                    key={w.id}
                    to={`/detail/${w.id}`}
                    className="px-3 py-1.5 rounded-md bg-background-100 border border-background-200 text-sm text-foreground-700 hover:border-primary-300 hover:text-primary-600 transition-colors cursor-pointer"
                  >
                    {w.title}
                  </Link>
                ))}
              </div>
            </div>
          </div>
        ) : (
          <>
            {/* 分类标签切换 */}
            <div className="inline-flex items-center gap-1 p-1 rounded-full bg-background-100 border border-background-200 mb-6">
              <button
                type="button"
                onClick={() => setTab("work")}
                className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
                  tab === "work"
                    ? "bg-primary-500 text-background-50"
                    : "text-foreground-600 hover:text-primary-600"
                }`}
              >
                影视作品 ({workResults.length})
              </button>
              <button
                type="button"
                onClick={() => setTab("news")}
                className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
                  tab === "news"
                    ? "bg-primary-500 text-background-50"
                    : "text-foreground-600 hover:text-primary-600"
                }`}
              >
                资讯 ({newsResults.length})
              </button>
            </div>

            {activeResults.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-16 text-center">
                <span className="w-16 h-16 rounded-full bg-background-100 text-foreground-400 flex items-center justify-center mb-4">
                  <i className="ri-emotion-sad-line text-3xl" />
                </span>
                <h2 className="font-heading text-lg font-semibold text-foreground-800">
                  该分类下暂无匹配结果
                </h2>
                <p className="mt-1 text-sm text-foreground-500">
                  换个关键词，或试试其他分类标签
                </p>
                <div className="mt-6">
                  <span className="text-sm text-foreground-500">为你推荐</span>
                  <div className="mt-3 flex flex-wrap justify-center gap-2">
                    {worksList.slice(0, 4).map((w) => (
                      <Link
                        key={w.id}
                        to={`/detail/${w.id}`}
                        className="px-3 py-1.5 rounded-md bg-background-100 border border-background-200 text-sm text-foreground-700 hover:border-primary-300 hover:text-primary-600 transition-colors cursor-pointer"
                      >
                        {w.title}
                      </Link>
                    ))}
                  </div>
                </div>
              </div>
            ) : tab === "work" ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4 md:gap-5">
                {workResults.map((w) => (
                  <WorkCard key={w.id} work={w} />
                ))}
              </div>
            ) : (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-x-10 rounded-lg border border-background-200 bg-background-50 px-5">
                {newsResults.map((n) => (
                  <NewsItem key={n.id} news={n} />
                ))}
              </div>
            )}
          </>
        )}
      </section>
    </Layout>
  );
}